import React from 'react';
import { makeStyles, rgba } from 'design-system/styles';
import { TextInput } from 'design-system';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { MenuList, MenuItem, Popover, Tooltip } from '@material-ui/core';
// import FeatureIcon from './FeatureIcon';
// import { OrgModeGroup, DefaultLabelColor } from './color';
import { getTextLines } from '@scale/shared/documenttranscription/text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Fuse from 'fuse.js';
import {
  getLabelColorPalette,
  INamedEntityRecognitionLabel,
  IColorMap,
} from './namedEntityRecognitionLabels';
import { depthFirstLabels } from '@scale/shared/namedentityrecognition/label';
import _ from 'lodash';

interface NERLabelMenuProps {
  labels: INamedEntityRecognitionLabel[];
  labelColormap: IColorMap;
  onSelect: (label: INamedEntityRecognitionLabel) => void;
  onResize: () => void;
  labelSearchIndex: Fuse<INamedEntityRecognitionLabel>;
}

// The NERLabelMenu component graphically can only represent one layer of nesting in the ontology.
// Thus, we need to "flatten" the ontology in order to represent it.
interface LabelMenuGroup {
  parentGroups: INamedEntityRecognitionLabel[];
  name: string;
  displayName: string;
  labels: INamedEntityRecognitionLabel[];
}

const groupLabelsForLabelMenu = (
  labels: INamedEntityRecognitionLabel[],
  parentGroups: INamedEntityRecognitionLabel[] = [],
): LabelMenuGroup[] => {
  const [groupLabels, trueLabels] = _.partition(labels, label => label.children);
  return _.concat(
    trueLabels.length
      ? {
          parentGroups,
          name: _.join(
            parentGroups.map(x => x.name),
            '/',
          ),
          displayName: _.join(
            parentGroups.map(x => x.display_name ?? x.name),
            ' / ',
          ),
          labels: trueLabels,
        }
      : [],
    _.flatMap(groupLabels, label =>
      groupLabelsForLabelMenu(label.children!, _.concat(parentGroups, label)),
    ),
  );
};

const NERLabelMenu: React.FC<NERLabelMenuProps> = props => {
  const { labels, labelColormap, onSelect, onResize, labelSearchIndex } = props;
  const classes = useLabelMenuStyles();
  const [search, setSearch] = React.useState('');
  const [openDropdownMenu, setOpenDropdownMenu] = React.useState<LabelMenuGroup | null>(null);
  const anchorEl = React.useRef<HTMLElement>();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setOpenDropdownMenu(null);
    onResize();
  };

  const onFocus = () => {
    setOpenDropdownMenu(null);
  };

  const searchResultLabels = React.useMemo(() => {
    if (!search) {
      return [];
    }
    return labelSearchIndex.search(search).map(result => result.item);
  }, [labelSearchIndex, search]);

  const labelColors = React.useMemo(
    () =>
      _.mapValues(
        _.keyBy(Array.from(depthFirstLabels(labels), label => label.name)),
        labelName => getLabelColorPalette(labelName, labelColormap).listItemBackground,
      ),
    [labels, labelColormap],
  );

  const labelGroups = React.useMemo(() => groupLabelsForLabelMenu(labels), [labels]);

  return (
    <div className={classes.menuWrapper}>
      <div style={{ padding: 16, paddingBottom: 8 }}>
        <TextInput
          placeholder="Filter labels..."
          value={search}
          onChange={onChange}
          fullWidth
          autoFocus
          onFocus={onFocus}
        />
      </div>
      {!!search && (
        <NERLabelMenuList
          labels={searchResultLabels}
          onSelect={onSelect}
          labelColors={labelColors}
        />
      )}
      {!search &&
        _.flatMap(labelGroups, labelGroup => {
          if (!labelGroup.parentGroups.length) {
            return labelGroup.labels.map(label => (
              <NERLabelMenuItem
                key={label.name}
                label={label}
                onSelect={onSelect}
                labelColors={labelColors}
              />
            ));
          }

          const onClick = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            anchorEl.current = event.currentTarget;
            setOpenDropdownMenu(labelGroup);
          };
          return (
            <MenuItem
              key={labelGroup.name}
              className={classes.itemName}
              onClick={onClick}
              selected={labelGroup === openDropdownMenu}
            >
              {labelGroup.displayName}
              <div className={classes.dropdownIcon}>
                <FontAwesomeIcon icon="chevron-right" />
              </div>
            </MenuItem>
          );
        })}
      {!!openDropdownMenu && (
        <Popover
          elevation={9}
          open={!!openDropdownMenu}
          anchorEl={anchorEl.current}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          onClose={() => {
            setOpenDropdownMenu(null);
          }}
          BackdropProps={{ invisible: true }}
          classes={{ root: classes.popover }}
        >
          <NERLabelMenuList
            labels={openDropdownMenu?.labels}
            onSelect={onSelect}
            labelColors={labelColors}
          />
        </Popover>
      )}
    </div>
  );
};

NERLabelMenu.displayName = 'NERLabelMenu';

interface NERLabelMenuListProps {
  labels: INamedEntityRecognitionLabel[];
  labelColors: Record<string, string>;
  onSelect: (feature: INamedEntityRecognitionLabel) => void;
}

const NERLabelMenuList: React.FC<NERLabelMenuListProps> = props => {
  const { labels, onSelect, labelColors } = props;

  const classes = useLabelMenuStyles();

  return (
    <MenuList classes={{ root: classes.menu }}>
      {labels.map(label => {
        /* if (!feature.label) {
          return null;
        } */
        return (
          <NERLabelMenuItem
            key={label.name}
            label={label}
            onSelect={onSelect}
            labelColors={labelColors}
          />
        );
      })}
    </MenuList>
  );
};

NERLabelMenuList.displayName = 'NERLabelMenuList';

interface NERLabelMenuItemProps {
  label: INamedEntityRecognitionLabel;
  onSelect: (feature: INamedEntityRecognitionLabel) => void;
  labelColors: Record<string, string>;
}

const NERLabelMenuItem: React.FC<NERLabelMenuItemProps> = props => {
  const { label, onSelect, labelColors } = props;

  const classes = useLabelMenuStyles();

  const onClick = () => {
    onSelect(label);
  };

  /* if (!feature.label) {
    return null;
  } */

  const fill = labelColors[label.name] || 'rgba(0,0,0,0.5)';

  const menuItem = (
    <MenuItem key={label.name} className={classes.itemName} onClick={onClick}>
      <div className={classes.listItemIcon} style={{ backgroundColor: fill }} />
      {label.display_name ?? label.name}
    </MenuItem>
  );

  let tooltipContent = null;
  if (label.description) {
    tooltipContent = getTextLines(label.description).map((text, i) => {
      return <div key={i}>{text}</div>;
    });
  }

  if (!tooltipContent) {
    return menuItem;
  }

  const title = <div style={{ maxWidth: 180 }}>{tooltipContent}</div>;

  return (
    <Tooltip
      key={`${label.name}-tooltip`}
      classes={{ tooltip: classes.tooltip }}
      title={title}
      placement="right"
    >
      {menuItem}
    </Tooltip>
  );
};

NERLabelMenuItem.displayName = 'NERLabelMenuItem';

const useLabelMenuStyles = makeStyles({
  menuWrapper: {
    minWidth: 240,
    paddingBottom: 8,
  },
  menu: {
    maxHeight: 320,
    overflowY: 'scroll',
    pointerEvents: 'all',
    minWidth: 240,
  },
  itemName: {
    fontSize: 14,
  },
  listItemIcon: {
    width: 16,
    height: 16,
    marginInlineEnd: '8px',
    borderRadius: 4,
  },
  dropdownIcon: {
    marginLeft: 'auto',
    display: 'inline-flex',
    width: 24,
    justifyContent: 'flex-end',
    opacity: 0.5,
    color: '#000',
    fontSize: 12,
  },
  icon: {
    display: 'inline-block',
  },
  popover: {
    pointerEvents: 'none',
  },
  tooltip: {
    background: rgba('#333333', 0.96),
  },
  softDisabled: {
    opacity: 0.5,
  },
});

export default NERLabelMenu;
